import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { EmptyState } from "@/components/common/EmptyState";
import { FilterTabs } from "@/pages/psychologist/tabs/patients/components/FilterTabs";
import { comunidadAdapter, FormacionDevolucion, FormacionDictada } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { activityTracker } from "@/utils/activityTracker";
import { CertificadoMiniatura } from "./components/CertificadoMiniatura";
import { DevolucionPuntaje } from "./components/DevolucionPuntaje";
import { downloadConstanciaDictado, formatCertificadoDate, formatCertificadoMonth } from "./certificadoFormacionPdf";

/** Pestañas del espacio, cada una con su slug (`/comunidad/dictadas[/devoluciones]`). */
const SECCIONES = [
  { id: "formaciones", label: "Formaciones dictadas" },
  { id: "devoluciones", label: "Devoluciones" },
];

const asistentesLabel = (n: number | null) =>
  n === null ? "Asistencia todavía sin cargar" : `${n} ${n === 1 ? "asistente" : "asistentes"}`;

/**
 * "Mis formaciones dictadas" (`/comunidad/dictadas`): solo para quien dictó al menos una
 * formación de Comunidad (se elige en el panel, "¿Quién la dicta?"). Historial con las
 * ediciones y cuántas asistieron, y el certificado de dictado de cada formación. Quien no dictó
 * ninguna vuelve a Comunidad: el backend solo devuelve las formaciones de la psicóloga logueada.
 */
export const FormacionesDictadasContent = () => {
  const navigate = useNavigate();
  const { seccion } = useParams<{ seccion?: string }>();
  const active = seccion === "devoluciones" ? "devoluciones" : "formaciones";
  const [dictadas, setDictadas] = useState<FormacionDictada[] | null>(null);
  const [error, setError] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  // Devoluciones publicadas (puntaje + resumen anónimo, revisados en el panel de admin).
  const [devoluciones, setDevoluciones] = useState<FormacionDevolucion[] | null>(null);
  const [devolucionesError, setDevolucionesError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    comunidadAdapter
      .listDictadas()
      .then((data) => { if (!cancelled) setDictadas(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (active !== "devoluciones" || devoluciones !== null) return;
    let cancelled = false;
    comunidadAdapter
      .listDevoluciones()
      .then((data) => { if (!cancelled) setDevoluciones(data); })
      .catch(() => { if (!cancelled) setDevolucionesError(true); });
    return () => { cancelled = true; };
  }, [active, devoluciones]);

  // Un slug que no existe vuelve a la pestaña principal.
  if (seccion && seccion !== "devoluciones") return <Navigate to="/comunidad/dictadas" replace />;
  if (dictadas !== null && dictadas.length === 0) return <Navigate to="/comunidad" replace />;

  const descargar = async (f: FormacionDictada) => {
    setDownloadingKey(f.formacionKey);
    try {
      await downloadConstanciaDictado(f.constancia);
      activityTracker.trackAction("comunidad_constancia_dictado_descargada");
    } catch {
      toastUtils.error("No pudimos armar el certificado. Probá de nuevo en un rato.");
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <div className="min-h-full w-full bg-[#E6DBFA]">
      <div className="mx-auto flex max-w-[960px] flex-col gap-6 px-4 py-6 md:px-8 md:py-10">
        <button
          type="button"
          onClick={() => navigate("/comunidad")}
          className="flex items-center gap-1.5 self-start text-sm font-semibold text-[#665090]"
        >
          <ArrowLeft size={16} />
          Volver a Comunidad
        </button>

        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-2xl font-extrabold text-[#2D2049]">Mis formaciones dictadas 🎓</h1>
          <p className="m-0 text-sm text-[#4A3D73]">Gracias por compartir lo que sabés con la red.</p>
        </div>

        <FilterTabs
          tabs={SECCIONES}
          activeTab={active}
          onTabChange={(id) => navigate(id === "devoluciones" ? "/comunidad/dictadas/devoluciones" : "/comunidad/dictadas")}
        />

        {error ? (
          <p className="m-0 text-sm text-gray-500">No pudimos cargar tus formaciones. Probá de nuevo en un rato.</p>
        ) : dictadas === null ? (
          <div className="flex justify-center py-10"><CircularProgress /></div>
        ) : active === "devoluciones" ? (
          devolucionesError ? (
            <p className="m-0 text-sm text-gray-500">No pudimos cargar tus devoluciones. Probá de nuevo en un rato.</p>
          ) : devoluciones === null ? (
            <div className="flex justify-center py-10"><CircularProgress /></div>
          ) : devoluciones.length === 0 ? (
            <EmptyState
              title="Todavía no hay devoluciones publicadas"
              description="Cuando se publique la devolución de una formación que dictaste, la vas a ver acá."
            />
          ) : (
            <div className="flex flex-col gap-4">
              <p className="m-0 text-xs text-[#4A3D73]">
                Es lo que compartieron quienes asistieron a las dos ediciones, de forma anónima.
              </p>
              {devoluciones.map((d) => (
                <div
                  key={d.formacionKey}
                  data-devolucion={d.formacionKey}
                  className="flex flex-col gap-4 rounded-[20px] bg-white p-5 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">
                      {formatCertificadoMonth(d.startsAt)}
                    </span>
                    <h2 className="m-0 text-lg font-bold leading-snug text-[#2D2049]">{d.title}</h2>
                  </div>
                  <DevolucionPuntaje puntaje={d.puntaje} />
                  <p className="m-0 whitespace-pre-line text-[14.5px] leading-relaxed text-[#4A3D73]">{d.resumen}</p>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="flex flex-col gap-4">
            <p className="m-0 text-xs text-[#4A3D73]">
              Como asistentes contamos solo a quienes se quedaron al menos el 75% del encuentro.
            </p>
            {dictadas.map((f) => (
              <div
                key={f.formacionKey}
                data-formacion={f.formacionKey}
                className="grid grid-cols-1 gap-5 rounded-[20px] bg-white p-5 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:grid-cols-[1fr_280px] md:p-6"
              >
                <div className="flex min-w-0 flex-col gap-3">
                  <h2 className="m-0 text-lg font-bold leading-snug text-[#2D2049]">{f.title}</h2>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {f.ediciones.map((e) => (
                      <li
                        key={e.edition}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-[#F5F1FF] px-3 py-2 text-sm text-[#4A3D73]"
                      >
                        <span className="font-semibold text-[#665090]">{e.edition}ª edición</span>
                        <span>{formatCertificadoDate(e.startsAt)}</span>
                        <span className="text-gray-400">·</span>
                        <span>{e.durationMinutes} minutos</span>
                        <span className="text-gray-400">·</span>
                        <span className={e.asistentes === null ? "text-gray-400" : "font-semibold"}>
                          {asistentesLabel(e.asistentes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col gap-3">
                  <CertificadoMiniatura certificado={f.constancia} variant="dictado" />
                  <Button
                    variant="outline"
                    className="border-[#C9BCE8] text-[#665090]"
                    onClick={() => descargar(f)}
                    disabled={downloadingKey === f.formacionKey}
                  >
                    <Download size={15} className="mr-1.5" />
                    {downloadingKey === f.formacionKey ? "Armando el PDF…" : "Descargar certificado"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormacionesDictadasContent;
